import React, { useState } from "react";
import Swal from "sweetalert2";
import { Modal, Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";

import PageTitle from "../../../layouts/PageTitle";
import { createSkuCategoryApi, updateSkuApi } from "../../../../services/inventory";
import { SkuBulkUploadModal } from "./BulkUploadModals";
import { appApi, useGetSkusQuery } from "../../../../store/appApi";
import { selectUser } from "../../../../store/authSlice";
import measurements from "./measurement.json";

const EMPTY_FORM = { skuName: "", measurement: "" };

const CreateSku = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector(selectUser);

  const [form, setForm] = useState(EMPTY_FORM);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // edit modal state
  const [editModal, setEditModal] = useState(false);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [editErrors, setEditErrors] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [editId, setEditId] = useState(null);

  const {
    data: skuData,
    isLoading: skuLoading,
    isFetching: skuFetching,
  } = useGetSkusQuery();

  const skus = Array.isArray(skuData?.skus) ? skuData.skus : [];
  const tableLoading = skuLoading || skuFetching;

  const [search, setSearch] = useState("");
  const normName = (s) => String(s || "").trim().replace(/\s+/g, " ").toLowerCase();
  const nameCounts = {};
  skus.forEach((s) => {
    const k = normName(s.skuName);
    nameCounts[k] = (nameCounts[k] || 0) + 1;
  });
  const filteredSkus = skus.filter((s) =>
    normName(s.skuName).includes(normName(search)),
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = (data, selfId = null) => {
    const e = {};
    if (!data.skuName.trim()) e.skuName = "SKU name is required.";
    else if (skus.some(
        (s) => s._id !== selfId && normName(s.skuName) === normName(data.skuName),
      ))
      e.skuName = "This SKU already exists.";
    if (!data.measurement) e.measurement = "Measurement unit is required.";
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate(form);
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return;
    }

    try {
      setLoading(true);
      await createSkuCategoryApi({
        skuName: form.skuName.trim(),
        measurement: form.measurement,
      });
      dispatch(appApi.util.invalidateTags(["SKUs"]));
      setForm(EMPTY_FORM);
      setErrors({});
      Swal.fire({
        icon: "success",
        title: "SKU Created",
        text: `"${form.skuName.trim()}" has been added.`,
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to create SKU. Please try again.";
      Swal.fire({ icon: "error", title: "Error", text: msg });
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (sku) => {
    setEditId(sku._id);
    setEditForm({ skuName: sku.skuName || "", measurement: sku.measurement || "" });
    setEditErrors({});
    setEditModal(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
    setEditErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate(editForm, editId);
    if (Object.keys(validationErrors).length) {
      setEditErrors(validationErrors);
      return;
    }

    try {
      setEditLoading(true);
      await updateSkuApi({
        _id: editId,
        skuName: editForm.skuName.trim(),
        measurement: editForm.measurement,
      });
      dispatch(appApi.util.invalidateTags(["SKUs"]));
      setEditModal(false);
      Swal.fire({
        icon: "success",
        title: "SKU Updated",
        text: `"${editForm.skuName.trim()}" has been updated.`,
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to update SKU. Please try again.";
      Swal.fire({ icon: "error", title: "Error", text: msg });
    } finally {
      setEditLoading(false);
    }
  };

  const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString("en-IN", { dateStyle: "medium" }) : "—";

  return (
    <>
      <PageTitle activeMenu="Create SKU" motherMenu="Inventory / Store" />
      <SkuBulkUploadModal
        show={bulkOpen}
        onHide={() => setBulkOpen(false)}
        onDone={() => dispatch(appApi.util.invalidateTags(["SKUs"]))}
      />

      <div className="row g-3">
        {/* ── Form ──────────────────────────────────────────────────────────── */}
        <div className="col-xl-4 col-lg-5">
          <div className="card" style={{ position: "sticky", top: 0 }}>
            <div className="card-header">
              <h5 className="card-title mb-0">
                <i className="fa fa-tag me-2 text-primary" />
                New SKU / Item
              </h5>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit} noValidate>
                {/* SKU Name */}
                <div className="form-group mb-3">
                  <label className="form-label" htmlFor="skuName">
                    Item Name <span className="text-danger">*</span>
                  </label>
                  <input
                    id="skuName"
                    name="skuName"
                    type="text"
                    className={`form-control ${errors.skuName ? "is-invalid" : ""}`}
                    placeholder="e.g. Diesel, Cement, Steel Rod"
                    value={form.skuName}
                    onChange={handleChange}
                    disabled={loading}
                  />
                  {errors.skuName && (
                    <div className="invalid-feedback">{errors.skuName}</div>
                  )}
                </div>

                {/* Measurement */}
                <div className="form-group mb-3">
                  <label className="form-label" htmlFor="measurement">
                    Measurement Unit <span className="text-danger">*</span>
                  </label>
                  <select
                    id="measurement"
                    name="measurement"
                    className={`form-control ${errors.measurement ? "is-invalid" : ""}`}
                    value={form.measurement}
                    onChange={handleChange}
                    disabled={loading}
                  >
                    <option value="">— Select Unit —</option>
                    {measurements.measurements.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                  {errors.measurement && (
                    <div className="invalid-feedback">{errors.measurement}</div>
                  )}
                </div>

                {/* Added By (read-only) */}
                <div className="form-group mb-4">
                  <label className="form-label">Added By</label>
                  <input
                    type="text"
                    className="form-control"
                    value={currentUser?.name || "—"}
                    readOnly
                    disabled
                  />
                </div>

                <button
                  type="submit"
                  className="btn btn-primary w-100"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Saving…
                    </>
                  ) : (
                    <>
                      <i className="fa fa-plus me-2" />
                      Add SKU
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* ── SKU List ──────────────────────────────────────────────────────── */}
        <div className="col-xl-8 col-lg-7">
          <div className="card">
            <div className="card-header d-flex align-items-center gap-2">
              <h5 className="card-title mb-0">Existing SKUs</h5>
              {!tableLoading && (
                <span className="badge badge-primary">{skus.length}</span>
              )}
              <input
                type="search"
                className="form-control form-control-sm ms-auto"
                style={{ maxWidth: 220 }}
                placeholder="Search SKU…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => setBulkOpen(true)}
              >
                <i className="fa fa-upload me-1" /> Bulk Upload
              </button>
            </div>
            <div
              className="card-body"
              style={{ overflowY: "auto", maxHeight: "calc(100vh - 250px)" }}
            >
              <div className="table-responsive" style={{ minWidth: 0 }}>
                <table className="display dataTable no-footer w-100">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Item Name</th>
                      <th>Unit</th>
                      <th>Created</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableLoading && (
                      <tr>
                        <td colSpan={5} className="text-center py-4">
                          <span className="spinner-border spinner-border-sm me-2" />
                          Loading…
                        </td>
                      </tr>
                    )}
                    {!tableLoading && filteredSkus.length === 0 && (
                      <tr>
                        <td colSpan={5} className="text-center py-4 text-muted">
                          No SKUs found. Use the form to add one.
                        </td>
                      </tr>
                    )}
                    {!tableLoading &&
                      filteredSkus.map((sku, idx) => (
                        <tr key={sku._id || idx}>
                          <td className="text-muted small">{idx + 1}</td>
                          <td className="fw-semibold">{sku.skuName || "—"}
                            {nameCounts[normName(sku.skuName)] > 1 && (
                              <span className="badge badge-danger ms-2">
                                Duplicate
                              </span>
                            )}
                          </td>
                          <td>
                            <span className="badge badge-light">
                              {sku.measurement || "—"}
                            </span>
                          </td>
                          <td className="text-muted small">
                            {formatDate(sku.created || sku.createdAt)}
                          </td>
                          <td>
                            <button
                              className="btn btn-xs btn-outline-primary"
                              onClick={() => openEditModal(sku)}
                            >
                              <i className="fa fa-pencil me-1" />
                              Edit
                            </button>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Edit SKU Modal ────────────────────────────────────────────────── */}
      <Modal show={editModal} onHide={() => setEditModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit SKU</Modal.Title>
        </Modal.Header>
        <form onSubmit={handleEditSubmit} noValidate>
          <Modal.Body>
            <div className="form-group mb-3">
              <label className="form-label" htmlFor="edit-skuName">
                Item Name <span className="text-danger">*</span>
              </label>
              <input
                id="edit-skuName"
                name="skuName"
                type="text"
                className={`form-control ${editErrors.skuName ? "is-invalid" : ""}`}
                value={editForm.skuName}
                onChange={handleEditChange}
                disabled={editLoading}
              />
              {editErrors.skuName && (
                <div className="invalid-feedback">{editErrors.skuName}</div>
              )}
            </div>

            <div className="form-group mb-3">
              <label className="form-label" htmlFor="edit-measurement">
                Measurement Unit <span className="text-danger">*</span>
              </label>
              <select
                id="edit-measurement"
                name="measurement"
                className={`form-control ${editErrors.measurement ? "is-invalid" : ""}`}
                value={editForm.measurement}
                onChange={handleEditChange}
                disabled={editLoading}
              >
                <option value="">— Select Unit —</option>
                {measurements.measurements.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
              {editErrors.measurement && (
                <div className="invalid-feedback">{editErrors.measurement}</div>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setEditModal(false)}
              disabled={editLoading}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={editLoading}>
              {editLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Saving…
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>
    </>
  );
};

export default CreateSku;
